import { Injectable } from '@angular/core';
import { ApiResponse, ApiService } from '../api.service';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of, startWith, switchMap } from 'rxjs';
import { GetUserData, GetUserOrgData, OrganizationMembership, User } from '../../interfaces/user';
import { AuthService } from '../auth/auth.service';

/**
 * UserService manages the authenticated user's basic data. It is maintained
 * internally as a BehaviorSubject and exposed to consumers as an Observable stream,
 * or a synchronous snapshot. These BehaviorSubjects are subscribed to the AuthService's
 * authUserData$ observable stream, and only fetch user data on changes to that stream, so
 * as long as the user is authenticated, the user data in this class will be populated and
 * up to date.
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _user$ = new BehaviorSubject<User | null>(null);
  readonly user$ = this._user$.asObservable();

  private _userOrgs$ = new BehaviorSubject<OrganizationMembership[] | null>(null);
  readonly userOrgs$ = this._userOrgs$.asObservable();

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {
    // Re-fetch whenever the auth context changes OR AuthService signals a refresh
    // (e.g. after EULA acceptance, which doesn't rotate the token). startWith so the
    // initial authUserData$ value still triggers a fetch before any refresh fires.
    const authContext$ = combineLatest([
      this.authService.authUserData$,
      this.authService.userDataRefresh$.pipe(startWith(undefined)),
    ]).pipe(map(([authData]) => authData));

    authContext$
      .pipe(
        switchMap((authData) => {
          if (!authData) {
            return of(null);
          }

          return this.getUser(authData.userId).pipe(
            catchError((err) => {
              console.error('Error fetching user data:', err);
              return of(null);
            })
          );
        })
      )
      .subscribe((user) => this._user$.next(user));

    authContext$
      .pipe(
        switchMap((authData) => {
          if (!authData) {
            return of(null);
          }

          return this.getUserOrgs().pipe(
            catchError((err) => {
              console.error('Error fetching user org data:', err);
              return of(null);
            })
          );
        })
      )
      .subscribe((userOrgs) => this._userOrgs$.next(userOrgs));
  }

  // synchronous snapshot of the current user value
  get user(): User | null {
    return this._user$.value;
  }

  private getUser(userId: string): Observable<User> {
    return this.apiService.get<ApiResponse<GetUserData>>(`/v1/users/${userId}`).pipe(
      map((response) => {
        return {
          id: String(response.data.UserID),
          name: response.data.Name,
          role: response.data.Role,
          email: response.data.Email,
          orgName: response.data.OrganizationName,
          phone: response.data.PhoneNumber,
        };
      })
    );
  }

  private getUserOrgs(): Observable<OrganizationMembership[]> {
    return this.apiService.get<ApiResponse<GetUserOrgData[]>>(`/v1/users/me/organizations`).pipe(
      map((response) => {
        return response.data.map(({ OrgID, orgName, membership_id, role, isPrimary }) => {
          return { orgId: String(OrgID), orgName, membershipId: String(membership_id), role, isPrimary };
        });
      })
    );
  }
}
